import {
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
  inject
} from "@angular/core";
import { DateDataField, DateHelper, Model, Store } from "@bryntum/core-thin";
import {
  ActionColumnConfig,
  ColumnConfig,
  DateColumn,
  DateColumnConfig,
  FilterConfig,
  Grid,
  GridConfig,
  NumberColumnConfig,
  RowExpanderConfig,
  TemplateColumnConfig,
  WidgetColumnConfig
} from "@bryntum/grid-thin";
import { ExcelExporter } from "@bryntum/grid-thin";
import writeXlsxFile, {
  SheetData,
  SheetOptions
} from "write-excel-file/browser";
import { CustomAjaxStore } from "@app/view/shared/base-pages/base-data-table-page/custom-ajax-store.component";

// Ensure ExcelExporter feature is registered (side-effect import, prevent tree-shaking)
void ExcelExporter;

// Bryntum's WriteExcelFileProvider expects writeXlsxFile on globalThis and
// calls it with the write-excel-file v3 signature:
//   writeXlsxFile(data, { fileName, dateFormat, columns })
// In write-excel-file v4 the call is lazy — it returns { toBlob, toFile } and
// nothing is generated (no download, no error) until toFile() is invoked — so
// adapt the v3-style call Bryntum makes to the v4 API here.
globalThis.writeXlsxFile = (
  data: SheetData,
  options: SheetOptions<Blob> & { fileName?: string } = {}
) => {
  const { fileName, ...sheetOptions } = options;
  const result = writeXlsxFile(data, sheetOptions);
  // v3 returned a Blob promise when no fileName was given; the caller owns
  // any rejection on this path.
  if (!fileName) {
    return result.toBlob();
  }
  // Bryntum's provider discards the returned promise, so a rejection here
  // would otherwise surface only as an unhandled rejection — log it instead.
  return result
    .toFile(fileName)
    .catch((error: unknown) =>
      console.error("Excel export failed to generate file:", error)
    );
};

// Bryntum 7 compatibility: ISO 8601 date string handling.
//
// 1) DateDataField.convert() — for simple (non-dotted) date fields that get
//    auto-added by DateColumn.construct(). The column's display format
//    (e.g. "DD/MM/YYYY HH:mm") is used as the parse format, which fails on
//    ISO strings. Fall back to native Date parsing.
const originalConvert = DateDataField.prototype.convert;
DateDataField.prototype.convert = function (value: any): any {
  const result = originalConvert.call(this, value);
  if (result === undefined && typeof value === "string" && value !== "") {
    const nativeParsed = new Date(value);
    if (!isNaN(nativeParsed.getTime())) {
      return nativeParsed;
    }
  }
  return result;
};

// 2) DateColumn.formatValue() — for complex-mapped fields (dot notation like
//    "job.planningWindowEnd") where no DateDataField is auto-added. The raw
//    ISO string reaches the renderer directly. Fall back to native parsing.
const originalFormatValue = (DateColumn.prototype as any).formatValue;
(DateColumn.prototype as any).formatValue = function (value: any): string {
  if (typeof value === "string") {
    const parsed = DateHelper.parse(value, this.format || undefined);
    if (!parsed || isNaN(parsed.getTime())) {
      const nativeParsed = new Date(value);
      if (!isNaN(nativeParsed.getTime())) {
        return DateHelper.format(nativeParsed, this.format || undefined);
      }
    }
  }
  return originalFormatValue.call(this, value);
};

const featureInputs = [
  "cellEdit",
  "cellTooltip",
  "columnDragToolbar",
  "columnPicker",
  "columnReorder",
  "columnResize",
  "contextMenu",
  "filter",
  "filterBar",
  "group",
  "groupSummary",
  "quickFind",
  "regionResize",
  "search",
  "sort",
  "stripe",
  "summary",
  "tree",
  "rowExpander"
];

const configInputs = [
  "autoHeight",
  "columns",
  "data",
  "emptyText",
  "enableTextSelection",
  "hideHeaders",
  "readOnly",
  "rowHeight",
  "store",
  "selectionMode",
  "showRemoveRowInContextMenu",
  "showDirty",
  "width",
  "showContextMenu"
];

@Component({
  selector: "app-grid",
  template: ""
})
export class GridComponent implements OnInit, OnChanges {
  public gridEngine: Grid;
  // #region Configs
  @Input() autoHeight: boolean;
  @Input() columns: (
    | TemplateColumnConfig
    | ColumnConfig
    | DateColumnConfig
    | WidgetColumnConfig
    | NumberColumnConfig
    | ActionColumnConfig
  )[];
  @Input() data: object[];
  @Input() emptyText: string;
  @Input() enableTextSelection: boolean;
  @Input() hideHeaders: boolean;
  @Input() readOnly: boolean;
  @Input() rowHeight: number;
  @Input() store: CustomAjaxStore<any>;
  @Input() selectionMode: object;
  @Input() width: string | object;
  @Input() showRemoveRowInContextMenu: boolean;
  @Input() showContextMenu: boolean;
  @Input() showDirty: boolean;
  // #region Features, only used for initialization
  @Input() cellEdit: boolean;
  // #endregion
  @Input() cellTooltip: boolean | object;
  @Input() columnDragToolbar: boolean;
  @Input() columnPicker: boolean;
  @Input() columnReorder: boolean;
  @Input() columnResize: boolean;
  @Input() contextMenu: boolean | object;
  @Input() filter: boolean | FilterConfig;
  @Input() filterBar: boolean | object;
  @Input() group: boolean | object | string;
  @Input() groupSummary: boolean;
  @Input() quickFind: boolean;
  @Input() regionResize: boolean;
  @Input() search: boolean;
  @Input() sort: boolean | string | object[];
  @Input() stripe: boolean;
  @Input() summary: boolean;
  @Input() rowExpander: Partial<RowExpanderConfig>;
  @Input() tree: boolean;
  @Input() cellClick: (event: any) => void;
  @Output() selectedRecord?: object = null;

  // @Input() rowExpander: Partial<RowExpanderConfig> | boolean = false;

  // #endregion
  @Output() selectedRecords?: Model[] = null;
  @Output() gridEvents = new EventEmitter<object>();
  @Output() totalRows: number;
  private elementRef: ElementRef;

  constructor() {
    const element = inject<ElementRef>(ElementRef);

    this.elementRef = element;
  }

  ngOnInit() {
    const // Features config object

      // Grid config object
      config: Partial<GridConfig> = {
        ripple: true,

        // Use this element as our encapsulating element
        adopt: this.elementRef.nativeElement,
        // store: this.store,
        // width: 'calc(100vw)',
        // height: 'calc(100vh)',
        // style: 'display: flex',
        // rowHeight: 24,
        // Listeners, will relay events
        listeners: {
          catchAll(event) {
            if (event.type === "selectionchange") {
              this.selectedRecord = this.gridEngine.selectedRecord;
              this.selectedRecords = this.gridEngine.selectedRecords;
            }

            this.gridEvents.emit(event);
          },
          cellClick: (event) => {
            if (this.cellClick) {
              this.cellClick(event);
            }
          },
          thisObj: this
        },
        features: {
          rowCopyPaste: false,
          excelExporter: true,
          cellMenu: {
            items: {
              removeRow: this.showRemoveRowInContextMenu
            }
          }
          // rowExpander: this.rowExpander
        }
      };
    // Pass configs on to grid
    configInputs.forEach((configName) => {
      if (configName in this) {
        config[configName] = this[configName];
      }
    });

    // Pass feature configs on to grid
    featureInputs.forEach((featureName) => {
      if (featureName in this) {
        config.features[featureName] = this[featureName];
      }
    });

    this.gridEngine = new Grid(config);

    // @ts-ignore
    this.gridEngine.columns.on("change", () => {
      this.gridEvents.emit({ type: "columnchange" });
    });
    this.gridEngine.on("show", () => {
      this.gridEvents.emit({ type: "show" });
    });

    this.gridEvents.emit({ type: "ready" });
  }

  ngOnChanges(changes: SimpleChanges) {
    if (this.gridEngine) {
      // Iterate over all changes
      Object.entries(changes).forEach(([name, { currentValue }]) => {
        // Apply changes that match configs to grid
        if (configInputs.includes(name)) {
          this.gridEngine[name] = currentValue;
        }
      });
    }
  }

  addRow(data): Model {
    // Add a row using the supplied data
    return (this.gridEngine.store as Store).add(data)[0];
  }

  clearGrid() {
    return (this.gridEngine.store as Store).removeAll(false);
  }

  removeRow() {
    // Remove selected row (if any)
    if (this.gridEngine.selectedRecord) {
      this.gridEngine.selectedRecord.remove();
    }
  }
}
